import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';
import DetailTabs from '../../components/DetailTabs/DetailTabs';
import Seo, { SITE_ORIGIN } from '../../components/Seo';
import { Breadcrumbs, Tag, plainText } from '../../components/ui';
import { CheckIcon, CloseIcon, ExternalIcon } from '../../components/ui/Icons';
import { useOverview } from '../../components/product/OverviewTab';
import { useDevices } from '../../contexts/DeviceContext';
import { getPlaceholderImage } from '../../utils/placeholder';
import { getPaymentMode, getVersionBadge, isWatchfaceProduct } from '../../utils/productType';
import { productSlug } from '../../hooks/useProducts';
import './WatchfaceDetailPage.css';
const ProductHero = ({ product }) => {
    const { t, i18n } = useTranslation();
    const overview = useOverview(product);
    const { selected, isCompatible } = useDevices();
    const compatible = isCompatible(product);
    const isWatchface = isWatchfaceProduct(product.is_vx);
    const mode = getPaymentMode(product.is_vx);
    const badge = getVersionBadge(product.is_vx);
    const rating = product.avgRating ? Number(product.avgRating) : null;
    const ratingText = rating !== null
        ? new Intl.NumberFormat(i18n.language || 'en', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rating)
        : '';
    return (_jsxs("section", { className: "product-hero", children: [_jsx("div", { className: "product-dial frame frame-trace", children: _jsx("img", { src: product.coverImage, alt: product.name, width: 360, height: 360, onError: (e) => {
                        e.target.src = getPlaceholderImage(product.name);
                    } }) }), _jsxs("div", { className: "product-hero-info", children: [_jsxs("div", { className: "product-hero-tags", children: [_jsx("span", { className: "ui-eyebrow", children: isWatchface ? t('pages.product.typeWatchface') : t('pages.product.typeApp') }), badge === 'combo' && _jsx(Tag, { children: "VX + Elite" }), badge === 'elite' && _jsx(Tag, { children: "Elite" }), badge === 'beta' && _jsx(Tag, { tone: "signal", children: "Beta" })] }), _jsx("h1", { className: "product-title", children: product.name }), overview?.tagline && _jsx("p", { className: "product-tagline", children: plainText(overview.tagline) }), _jsxs("dl", { className: "product-specs", children: [!!product.supportedDevicesCount && (_jsxs("div", { children: [_jsx("dt", { children: t('pages.product.devicesLabel') }), _jsx("dd", { children: product.supportedDevicesCount })] })), rating !== null && rating > 0 && (_jsxs("div", { children: [_jsx("dt", { children: "\u2605" }), _jsx("dd", { "aria-label": t('pages.product.rating', { value: ratingText }), children: ratingText })] })), mode === 1 && (product.vxVersion || product.eliteVersion) ? (_jsxs(_Fragment, { children: [product.vxVersion && (_jsxs("div", { children: [_jsx("dt", { children: "VX" }), _jsxs("dd", { children: ["v", product.vxVersion] })] })), product.eliteVersion && (_jsxs("div", { children: [_jsx("dt", { children: "Elite" }), _jsxs("dd", { children: ["v", product.eliteVersion] })] }))] })) : product.latestExternalVersion ? (_jsxs("div", { children: [_jsx("dt", { children: t('pages.product.version') }), _jsxs("dd", { children: ["v", product.latestExternalVersion] })] })) : null] }), selected && compatible !== null && (_jsxs("p", { className: `product-compat ${compatible ? 'ok' : 'no'}`, children: [compatible ? _jsx(CheckIcon, { size: 18 }) : _jsx(CloseIcon, { size: 18 }), t(compatible ? 'pages.devices.compatible' : 'pages.devices.notCompatible', { device: selected.name })] })), _jsxs("div", { className: "product-actions", children: [product.storelink && (_jsxs("a", { href: product.storelink, target: "_blank", rel: "noopener noreferrer", className: "btn", children: [t('pages.product.goToStore'), _jsx(ExternalIcon, { size: 16 })] })), product.triallink && product.triallink !== product.storelink && (_jsx("a", { href: product.triallink, target: "_blank", rel: "noopener noreferrer", className: "btn btn-outline", children: t('pages.product.tryVx') }))] }), i18n.language.startsWith('ru') && (mode === 0 || mode === 1) && (_jsx("p", { className: "russia-payment-warning", children: t('watchfaceDetail.russiaPaymentWarning', 'В данный момент из-за различных санкций некоторые или все методы оплаты могут не работать. Как разработчик я, к сожалению, не могу на это повлиять и не могу предложить собственные решения.') }))] })] }));
};
const WatchfaceDetailPage = () => {
    const { t } = useTranslation();
    const params = useParams();
    const idOrSlug = params.idOrSlug ?? params.slug;
    const [product, setProduct] = useState(null);
    const [colorThemes, setColorThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!idOrSlug)
            return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await apiService.getProductByIdOrWeblink(idOrSlug);
                if (cancelled)
                    return;
                if (!res.success) {
                    setError(res.error || 'not found');
                    return;
                }
                setProduct(res.data);
                const themes = await apiService.getColorThemes(res.data.id);
                if (!cancelled && themes.success)
                    setColorThemes(themes.data);
            }
            catch {
                if (!cancelled)
                    setError('error');
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [idOrSlug]);
    if (loading) {
        return (_jsxs("div", { className: "loading-container", children: [_jsx("div", { className: "loading-spinner" }), _jsx("p", { children: t('watchfaceDetail.loading', 'Loading…') })] }));
    }
    if (error || !product) {
        return (_jsxs("div", { className: "error-container", children: [_jsx("h2", { children: t('pages.product.notFound') }), _jsx(Link, { to: "/", className: "btn btn-outline", children: t('pages.product.backHome') })] }));
    }
    const slug = productSlug(product);
    const absoluteImage = product.coverImage
        ? product.coverImage.startsWith('http')
            ? product.coverImage
            : `${SITE_ORIGIN}${product.coverImage}`
        : undefined;
    const pageUrl = `${SITE_ORIGIN}/watchface/${slug}`;
    const description = plainText(t(`products.${slug}.tagline`, { defaultValue: '' }) || `${product.name} – TitanicTurtle for Garmin`);
    const productJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: product.name,
        applicationCategory: 'LifestyleApplication',
        operatingSystem: 'Garmin Connect IQ',
        description,
        ...(absoluteImage ? { image: absoluteImage } : {}),
        url: pageUrl,
        ...(product.avgRating && Number(product.avgRating) > 0
            ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: Number(product.avgRating).toFixed(1), bestRating: '5' } }
            : {}),
        ...(product.price ? { offers: { '@type': 'Offer', price: Number(product.price).toFixed(2), priceCurrency: 'EUR', url: pageUrl } } : {}),
    };
    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'TitanicTurtle', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: product.name, item: pageUrl },
        ],
    };
    return (_jsxs("div", { className: "product-page", children: [_jsx(Seo, { title: `${product.name} – Garmin Connect IQ`, description: description, canonicalPath: `/watchface/${slug}`, type: "product", image: absoluteImage, jsonLd: [productJsonLd, breadcrumbJsonLd] }), _jsxs("div", { className: "product-container", children: [_jsx(Breadcrumbs, { items: [{ label: t('pages.product.allProducts'), to: '/' }, { label: product.name }] }), _jsx(ProductHero, { product: product }), _jsx(DetailTabs, { product: product, colorThemes: colorThemes })] })] }));
};
export default WatchfaceDetailPage;
